
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { usePhotoEditor } from "react-photo-editor";
import _ from "lodash";
import { FileTypes } from "../../utils/appConstants";
import UploadIcon from "../../assets/svg/upload-article-icon.svg";


import CustomImage from "./Image";
import Modal from "./Modal";
import PenSVG from "../../assets/svg/PenSVG";
import { Button } from ".";
import { Slider } from "@material-ui/core";
import { DeleteOutlineOutlined } from "@material-ui/icons";
import { ReactComponent as CropIcon } from "../../assets/svg/crop-img.svg";
import { ReactComponent as FilterBAndWIcon } from "../../assets/svg/adjust-filters.svg";
import { ReactComponent as BrightnessMediumIcon } from "../../assets/svg/adjust-colors.svg";
import { ReactComponent as RotateLeftIcon } from "../../assets/svg/rotate-left.svg";
import { ReactComponent as RotateRightIcon } from "../../assets/svg/rotate-right.svg";
import { ReactComponent as FlipIcon } from "../../assets/svg/flip-horizontal.svg";


type MediaType = "image" | "video";

type EditHistory = {
  filterBrightness: number;
  filterContrast: number;
  filterSaturate: number;
  filterGrayscale: number;
  adjustBrightness: number;
  adjustContrast: number;
  adjustSaturate: number;
  adjustGrayscale: number;
  rotate: number;
  flipHorizontal: boolean;
  flipVertical: boolean;
  zoom: number;
  straighten: number;
  selectedFilter: string;
};

type PreviewState = null | {
  url: string;
  file?: File;
  originalFile?: File;
  originalUrl?: string;
  mediaType: MediaType;
  videoThumbnail?: string;
  videoThumbnailFile?: File | null;
  editHistory?: EditHistory;
};

interface Props {
  allowFileTypes?: string[];
  allowFileSize?: number;
  displayFileName?: boolean;
  existingImage?: string;
  originalImage?: string;
  existingMediaType?: MediaType;
  existingEditHistory?: EditHistory;
  autoOpenEdit?: boolean;

  onSuccess?: (
    file: File | null,
    originalFile?: File | null,
    mediaType?: MediaType,
    thumbnail?: string,
    editHistory?: EditHistory | undefined
  ) => void;
  onError?: (err: { type: string; message: string }) => void;
  onClose?: () => void;
  onDelete?: () => void;
}

const MAX_ZOOM = 3;

const EXT_TO_MIME: Record<string, string> = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  bmp: "image/bmp",
  svg: "image/svg+xml",
  mp4: "video/mp4",
  mov: "video/quicktime",
  webm: "video/webm",
  m4v: "video/x-m4v",
  mkv: "video/x-matroska",
  avi: "video/x-msvideo",
  ogv: "video/ogg",
  wmv: "video/x-ms-wmv",
};

const PREDEFINED_FILTERS = [
  { id: "original", name: "Original", settings: { brightness: 100, contrast: 100, saturate: 100, grayscale: 0 } },
  { id: "studio", name: "Studio", settings: { brightness: 108, contrast: 120, saturate: 85, grayscale: 0 } },
  { id: "prime", name: "Prime", settings: { brightness: 112, contrast: 130, saturate: 150, grayscale: 0 } },
  { id: "classic", name: "Classic", settings: { brightness: 98, contrast: 115, saturate: 75, grayscale: 0 } },
  { id: "edge", name: "Edge", settings: { brightness: 105, contrast: 155, saturate: 65, grayscale: 15 } },
];

const ArticleDropZone: React.FC<Props> = (props) => {
  const [fileTypeError, setFileTypeError] = useState<string>("");
  const [showModal, setShowModal] = useState<boolean>(false);
  const [previewFile, setPreviewFile] = useState<PreviewState>(null);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<"crop" | "filter" | "adjust">("crop");
  const [filterBrightness, setFilterBrightness] = useState<number>(100);
  const [filterContrast, setFilterContrast] = useState<number>(100);
  const [filterSaturate, setFilterSaturate] = useState<number>(100);
  const [filterGrayscale, setFilterGrayscale] = useState<number>(0);
  const [adjustBrightness, setAdjustBrightness] = useState<number>(100);
  const [adjustContrast, setAdjustContrast] = useState<number>(100);
  const [adjustSaturate, setAdjustSaturate] = useState<number>(100);
  const [straighten, setStraighten] = useState<number>(0);
  const [selectedFilter, setSelectedFilter] = useState<string>("original");
  const [zoomUI, setZoomUI] = useState(0);
  const uiToZoom = (ui: number) => 1 + ((MAX_ZOOM - 1) * ui) / 100;
  const zoomToUI = (z: number) => Math.round(((z - 1) / (MAX_ZOOM - 1)) * 100);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editCanvasRef = useRef<HTMLCanvasElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const thumbInputRef = useRef<HTMLInputElement>(null);
  const originalOnceRef = useRef<{ url: string; file: File } | null>(null);
  const setOriginalOnce = (file: File, url: string) => {
    if (!originalOnceRef.current) originalOnceRef.current = { file, url };
  };

  const {
    rotate,
    setRotate,
    flipHorizontal,
    setFlipHorizontal,
    flipVertical,
    setFlipVertical,
    zoom,
    setZoom,
    resetFilters: resetPhotoEditorFilters,
  } = usePhotoEditor({ file: previewFile?.originalFile || previewFile?.file });

  useEffect(() => {
    setZoom(uiToZoom(zoomUI));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoomUI]);

  const resetAllStates = () => {
    setStraighten(0);
    setZoom(1);
    setZoomUI(0);
    setRotate(0);
    setFlipHorizontal(false);
    setFlipVertical(false);

    setFilterBrightness(100);
    setFilterContrast(100);
    setFilterSaturate(100);
    setFilterGrayscale(0);

    setAdjustBrightness(100);
    setAdjustContrast(100);
    setAdjustSaturate(100);

    setSelectedFilter("original");
    setSelectedOption("crop");
    resetPhotoEditorFilters();
  };

  const revokeBlobUrl = (url?: string) => {
    if (url?.startsWith("blob:")) URL.revokeObjectURL(url);
  };

  const clearCanvas = () => {
    const canvas = editCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    canvas.width = 0;
    canvas.height = 0;
  };

  const clearPreviewSession = ({
    keepModalOpen = true,
  }: { keepModalOpen?: boolean } = {}) => {
    revokeBlobUrl(previewFile?.url);
    revokeBlobUrl(originalOnceRef.current?.url);
    originalOnceRef.current = null;
    setFileTypeError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (thumbInputRef.current) thumbInputRef.current.value = "";
    clearCanvas();
    setEditMode(false);
    setPreviewFile(null);
    if (!keepModalOpen) setShowModal(false);
  };

  const acceptAttr = useMemo(() => {
    if (!props.allowFileTypes || props.allowFileTypes.length === 0) return "image/*,video/*";
    return props.allowFileTypes.map((ext) => `.${ext.toLowerCase()}`).join(",");
  }, [props.allowFileTypes]);

  const getMediaType = (file: File): MediaType => (file.type.startsWith("video/") ? "video" : "image");

  const readAsDataURL = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result as string);
      fr.onerror = reject;
      fr.readAsDataURL(file);
    });

  const generateVideoThumbnail = useCallback((videoFile: File, timestamp = 0): Promise<string> => {
    return new Promise((resolve, reject) => {
      const v = document.createElement("video");
      const c = document.createElement("canvas");
      const ctx = c.getContext("2d");
      if (!ctx) return reject(new Error("Canvas context missing"));

      v.preload = "metadata";
      v.onloadedmetadata = () => {
        c.width = v.videoWidth || 640;
        c.height = v.videoHeight || 360;
        const t = Math.min(timestamp, v.duration || 0);
        v.currentTime = isFinite(t) && t > 0 ? t : 0.001;
      };
      v.onseeked = () => {
        ctx.drawImage(v, 0, 0, c.width, c.height);
        try {
          resolve(c.toDataURL("image/jpeg", 0.85));
        } catch (e) {
          reject(e);
        } finally {
          URL.revokeObjectURL(v.src);
        }
      };
      v.onerror = () => {
        URL.revokeObjectURL(v.src);
        reject(new Error("Video load error"));
      };
      v.src = URL.createObjectURL(videoFile);
    });
  }, []);

  const sourceUrlFromOriginal = () => {
    // Prefer originalFile (blob, no CORS issues) over URLs
    if (previewFile?.originalFile) {
      return URL.createObjectURL(previewFile.originalFile);
    }
    if (originalOnceRef.current?.file) {
      return URL.createObjectURL(originalOnceRef.current.file);
    }
    // Fallback to URLs (may have CORS issues)
    return originalOnceRef.current?.url ||
      previewFile?.originalUrl ||
      previewFile?.url ||
      "";
  };

  const applyAllTransforms = useCallback(() => {
    if (!editCanvasRef.current || !previewFile || previewFile.mediaType === "video") return;

    const canvas = editCanvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const renderImage = (img: HTMLImageElement) => {
      const container = canvas.parentElement;
      if (container && container.clientWidth > 0 && container.clientHeight > 0) {
        canvas.width = container.clientWidth;
        canvas.height = container.clientHeight;
      } else {
        // Container not ready, wait for next frame
        requestAnimationFrame(() => renderImage(img));
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.save();

      const baseScale = Math.min(canvas.width / img.width, canvas.height / img.height);
      const scale = baseScale * zoom;

      ctx.translate(canvas.width / 2, canvas.height / 2);
      if (straighten) ctx.rotate((straighten * Math.PI) / 180);
      if (rotate) ctx.rotate((rotate * Math.PI) / 180);
      if (flipHorizontal) ctx.scale(-1, 1);
      if (flipVertical) ctx.scale(1, -1);
      ctx.scale(scale, scale);

      ctx.filter = `brightness(${Math.round((filterBrightness / 100) * (adjustBrightness / 100) * 100)}%) ` +
        `contrast(${Math.round((filterContrast / 100) * (adjustContrast / 100) * 100)}%) ` +
        `saturate(${Math.round((filterSaturate / 100) * (adjustSaturate / 100) * 100)}%) ` +
        `grayscale(${filterGrayscale}%)`;

      ctx.drawImage(img, -img.width / 2, -img.height / 2);
      ctx.restore();
    };

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => renderImage(img);

    img.onerror = () => {
      // Fallback to previewFile.url if source fails
      if (previewFile.url) {
        const fallback = new Image();
        fallback.onload = () => renderImage(fallback);
        fallback.src = previewFile.url;
      }
    };

    const sourceUrl = sourceUrlFromOriginal();
    if (!sourceUrl) {
      console.error("No valid source URL found for image");
      return;
    }
    img.src = sourceUrl;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    previewFile, straighten, rotate, flipHorizontal, flipVertical, zoom,
    filterBrightness, filterContrast, filterSaturate, filterGrayscale,
    adjustBrightness, adjustContrast, adjustSaturate
  ]);

  useEffect(() => {
    if (editMode && previewFile?.mediaType === "image") {
      applyAllTransforms();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    editMode,
    applyAllTransforms,
    previewFile?.mediaType,
    filterBrightness,
    filterContrast,
    filterSaturate,
    filterGrayscale,
    adjustBrightness,
    adjustContrast,
    adjustSaturate,
  ]);

  useEffect(() => {
    if (!previewFile) {
      resetAllStates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewFile]);

  useEffect(() => {
    if (!props.existingImage || previewFile) return;

    let cancelled = false;
    setShowModal(true);

    const initialMediaType: MediaType = props.existingMediaType || "image";
    setPreviewFile({
      url: props.existingImage,
      originalUrl: props.originalImage || props.existingImage,
      mediaType: initialMediaType,
      editHistory: props.existingEditHistory || undefined,
    });

    (async () => {
      try {
        const currResp = await fetch(props.existingImage);
        const currBlob = await currResp.blob();
        const currFile = new File([currBlob], initialMediaType === "video" ? "current-video.mp4" : "current-image.jpg", {
          type: currBlob.type || (initialMediaType === "video" ? "video/mp4" : "image/jpeg"),
        });

        let originalUrl = props.originalImage || props.existingImage;
        let originalFile = currFile;

        if (props.originalImage && props.originalImage !== props.existingImage) {
          try {
            const origResp = await fetch(props.originalImage);
            const origBlob = await origResp.blob();
            originalFile = new File(
              [origBlob],
              initialMediaType === "video" ? "original-video.mp4" : "original-image.jpg",
              { type: origBlob.type || currBlob.type }
            );
          } catch (e) {
            console.warn("Could not fetch original media, using current as original:", e);
          }
        }

        if (cancelled) return;

        originalOnceRef.current = { url: originalUrl, file: originalFile };

        setPreviewFile((prev) =>
          prev
            ? {
              ...prev,
              file: currFile,
              originalFile,
              originalUrl,
              mediaType: initialMediaType,
              // For video, try to derive a poster if possible
              videoThumbnail: initialMediaType === "video" ? (prev.videoThumbnail || undefined) : prev?.videoThumbnail,
            }
            : prev
        );

        if (props.autoOpenEdit && initialMediaType === "image") {
          setTimeout(() => setEditMode(true), 100);
        }
      } catch (e) {
        console.error("Failed hydrating existing media:", e);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.existingImage,
    props.originalImage,
    props.existingEditHistory,
    props.autoOpenEdit,
    props.existingMediaType,
    previewFile
  ]);

  // Drop handling
  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      for (const file of acceptedFiles) {
        const { onError, allowFileTypes, allowFileSize } = props;
        setFileTypeError("");

        const fileType = file.type || "";
        const fileSize = file.size || 0;
        const mediaType: MediaType = getMediaType(file);

        // Validate types
        if (allowFileTypes && allowFileTypes.length > 0) {
          const allowedMime = new Set<string>();
          allowFileTypes.forEach((ext) => {
            const mime = EXT_TO_MIME[ext.toLowerCase()];
            if (mime) allowedMime.add(mime);
            if ((FileTypes as any)?.[ext]) allowedMime.add((FileTypes as any)[ext]);
          });
          if (allowedMime.size > 0 && !allowedMime.has(fileType)) {
            const message = `Only ${allowFileTypes.join(", ")} file types are allowed`;
            onError?.({ type: "invalid-file-type", message });
            setFileTypeError(message);
            return;
          }
        }

        // Validate size
        if (allowFileSize && fileSize > allowFileSize) {
          const mb = Math.floor(allowFileSize / 1024 / 1024);
          const message = `Max ${mb} MB file size allowed`;
          props.onError?.({ type: "max-file-upload-size", message });
          setFileTypeError(message);
          return;
        }

        revokeBlobUrl(originalOnceRef.current?.url);
        originalOnceRef.current = null;
        setEditMode(false);

        const fileURL = URL.createObjectURL(file);
        setOriginalOnce(file, fileURL);

        let videoThumbnail = "";
        if (mediaType === "video") {
          try {
            videoThumbnail = await generateVideoThumbnail(file, 0.5);
          } catch {
            // ignore
          }
        }

        // Revoke previous blob url if any
        setPreviewFile((prev) => {
          if (prev?.url?.startsWith("blob:")) URL.revokeObjectURL(prev.url);
          return {
            url: fileURL,
            originalUrl: originalOnceRef.current!.url,
            file,
            originalFile: originalOnceRef.current!.file,
            mediaType,
            videoThumbnail,
            videoThumbnailFile: null,
          };
        });

        if (!showModal) setShowModal(true);

        queueMicrotask(() => resetAllStates());
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props, showModal, generateVideoThumbnail]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: 1,
    noClick: true,
  });

  // Handlers
  const handleDropzoneClick = (e: React.MouseEvent) => {
    e.preventDefault();
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) onDrop([f]);
    e.currentTarget.value = "";
  };

  const getFilterPreviewImageUrl = () => {
    if (!previewFile) return "";
    if (previewFile.mediaType === "video") return previewFile.videoThumbnail || "";
    return previewFile.url;
  };

  const applyPredefinedFilter = (filterId: string) => {
    const f = PREDEFINED_FILTERS.find((x) => x.id === filterId);
    if (!f) return;
    setFilterBrightness(f.settings.brightness);
    setFilterContrast(f.settings.contrast);
    setFilterSaturate(f.settings.saturate);
    setFilterGrayscale(f.settings.grayscale);
    setSelectedFilter(filterId);

    // if (editMode && previewFile?.mediaType === "image") {
    //   setTimeout(() => applyAllTransforms(), 10);
    // }
  };

  const onEditClick = () => {
    if (previewFile?.editHistory) {
      const h = previewFile.editHistory;
      setFilterBrightness(h.filterBrightness);
      setFilterContrast(h.filterContrast);
      setFilterSaturate(h.filterSaturate);
      setFilterGrayscale(h.filterGrayscale);
      setAdjustBrightness(h.adjustBrightness);
      setAdjustContrast(h.adjustContrast);
      setAdjustSaturate(h.adjustSaturate);
      setRotate(h.rotate);
      setFlipHorizontal(h.flipHorizontal);
      setFlipVertical(h.flipVertical);
      setZoom(h.zoom);
      setZoomUI(zoomToUI(h.zoom));
      setStraighten(h.straighten);
      setSelectedFilter(h.selectedFilter);

      // Automatically show filter tab if a non-original filter was applied
      if (h.selectedFilter && h.selectedFilter !== "original") {
        setSelectedOption("filter");
      }

      // Set edit mode after a brief delay to ensure state updates are applied
      // and canvas container is ready for rendering
      setTimeout(() => {
        setEditMode(true);
      }, 0);
    } else {
      resetAllStates();
      setEditMode(true);
    }
  };

  const onCancelEdit = () => {
    if (previewFile?.editHistory && previewFile.mediaType === "image") {
      const h = previewFile.editHistory;
      setFilterBrightness(h.filterBrightness);
      setFilterContrast(h.filterContrast);
      setFilterSaturate(h.filterSaturate);
      setFilterGrayscale(h.filterGrayscale);
      setAdjustBrightness(h.adjustBrightness);
      setAdjustContrast(h.adjustContrast);
      setAdjustSaturate(h.adjustSaturate);
      setRotate(h.rotate);
      setFlipHorizontal(h.flipHorizontal);
      setFlipVertical(h.flipVertical);
      setZoom(h.zoom);
      setZoomUI(zoomToUI(h.zoom));
      setStraighten(h.straighten);
      setSelectedFilter(h.selectedFilter);
    } else {
      resetAllStates();
    }
    setEditMode(false);
  };

  const onResetAll = () => {
    resetAllStates();

    if (editCanvasRef.current && previewFile && previewFile.mediaType === "image") {
      const canvas = editCanvasRef.current;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      const img = new Image();
      img.onload = () => {
        const container = canvas.parentElement;
        if (container) {
          canvas.width = container.clientWidth;
          canvas.height = container.clientHeight;
        }
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.save();
        const baseScale = Math.min(canvas.width / img.width, canvas.height / img.height);
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.scale(baseScale, baseScale);
        ctx.filter = "none";
        ctx.drawImage(img, -img.width / 2, -img.height / 2);
        ctx.restore();
      };
      img.src = sourceUrlFromOriginal();
    }
  };

  const rotateLeft = () => setRotate(rotate - 90);
  const rotateRight = () => setRotate(rotate + 90);
  const flipHorizontalToggle = () => setFlipHorizontal(!flipHorizontal);
  const flipVerticalToggle = () => setFlipVertical(!flipVertical);

  const onApplyChanges = async () => {
    if (!previewFile) return;

    if (previewFile.mediaType === "video") {
      setEditMode(false);
      return;
    }

    if (!editCanvasRef.current) return;

    const currentEditHistory: EditHistory = {
      filterBrightness,
      filterContrast,
      filterSaturate,
      filterGrayscale,
      adjustBrightness,
      adjustContrast,
      adjustSaturate,
      adjustGrayscale: filterGrayscale,
      rotate,
      flipHorizontal,
      flipVertical,
      zoom,
      straighten,
      selectedFilter,
    };

    // Export method - load from original source (prefer File objects to avoid CORS)
    const exportFromOriginalImage = () => {
      const exportCanvas = document.createElement("canvas");
      const exportCtx = exportCanvas.getContext("2d");
      if (!exportCtx) {
        setPreviewFile((prev) => (prev ? { ...prev, editHistory: currentEditHistory } : prev));
        setEditMode(false);
        return;
      }

      const img = new Image();
      img.crossOrigin = "anonymous";

      img.onload = () => {
        try {
          exportCanvas.width = img.width;
          exportCanvas.height = img.height;

          exportCtx.clearRect(0, 0, exportCanvas.width, exportCanvas.height);
          exportCtx.save();
          exportCtx.translate(exportCanvas.width / 2, exportCanvas.height / 2);
          if (straighten) exportCtx.rotate((straighten * Math.PI) / 180);
          if (rotate) exportCtx.rotate((rotate * Math.PI) / 180);
          if (flipHorizontal) exportCtx.scale(-1, 1);
          if (flipVertical) exportCtx.scale(1, -1);
          exportCtx.scale(zoom, zoom);

          exportCtx.filter = `brightness(${Math.round((filterBrightness / 100) * (adjustBrightness / 100) * 100)}%) ` +
            `contrast(${Math.round((filterContrast / 100) * (adjustContrast / 100) * 100)}%) ` +
            `saturate(${Math.round((filterSaturate / 100) * (adjustSaturate / 100) * 100)}%) ` +
            `grayscale(${filterGrayscale}%)`;

          exportCtx.drawImage(img, -img.width / 2, -img.height / 2);
          exportCtx.restore();

          let dataUrl: string;
          try {
            dataUrl = exportCanvas.toDataURL("image/png", 0.95);
          } catch (e) {
            console.warn("Canvas tainted, cannot export edited image. Saving history only.");
            setPreviewFile((prev) => (prev ? { ...prev, editHistory: currentEditHistory } : prev));
            setEditMode(false);
            return;
          }

          const newFile = dataURLToFile(
            dataUrl,
            previewFile?.file?.name?.replace(/\.[^/.]+$/, ".png") || "edited.png"
          );
          const newURL = URL.createObjectURL(newFile);

          if (previewFile.url?.startsWith("blob:")) URL.revokeObjectURL(previewFile.url);

          setPreviewFile({
            url: newURL,
            originalUrl: originalOnceRef.current?.url || previewFile.originalUrl,
            file: newFile,
            originalFile: originalOnceRef.current?.file || previewFile.originalFile,
            mediaType: "image",
            editHistory: currentEditHistory,
          });

          setEditMode(false);
        } catch (e) {
          console.error("Error applying changes:", e);
          setPreviewFile((prev) => (prev ? { ...prev, editHistory: currentEditHistory } : prev));
          setEditMode(false);
        }
      };

      img.onerror = () => {
        console.warn("Cannot load image for export (CORS?). Saving history only.");
        setPreviewFile((prev) => (prev ? { ...prev, editHistory: currentEditHistory } : prev));
        setEditMode(false);
      };

      // Prefer File objects (blob URLs) to avoid CORS issues
      const sourceUrl = sourceUrlFromOriginal();

      if (!sourceUrl) {
        console.warn("No source URL available for export");
        setPreviewFile((prev) => (prev ? { ...prev, editHistory: currentEditHistory } : prev));
        setEditMode(false);
        return;
      }
      img.src = sourceUrl;
    };

    exportFromOriginalImage();
  };


  // Helpers
  const dataURLToFile = (dataURL: string, fileName = "edited.png") => {
    const [head, body] = dataURL.split(",");
    const mime = head.match(/:(.*?);/)?.[1] || "image/png";
    const bstr = atob(body);
    const u8 = new Uint8Array(bstr.length);
    for (let i = 0; i < bstr.length; i++) u8[i] = bstr.charCodeAt(i);
    return new File([u8], fileName, { type: mime });
  };

  const handleThumbnailFile = async (file: File) => {
    if (!file.type.startsWith("image/") || !previewFile) return;
    const dataURL = await readAsDataURL(file);
    setPreviewFile({ ...previewFile, videoThumbnail: dataURL, videoThumbnailFile: file });
  };

  const onThumbInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) handleThumbnailFile(f);
    e.currentTarget.value = "";
  };

  const removeUploadedThumbnail = async () => {
    if (!previewFile || previewFile.mediaType !== "video") return;
    try {
      const baseFile = (previewFile.originalFile || previewFile.file) as File;
      const fallback = await generateVideoThumbnail(baseFile, 0.5);
      setPreviewFile({ ...previewFile, videoThumbnail: fallback, videoThumbnailFile: null });
    } catch {
      setPreviewFile({ ...previewFile, videoThumbnailFile: null });
    }
  };

  const openThumbPicker = () => thumbInputRef.current?.click();

  const closeModal = () => {
    clearPreviewSession({ keepModalOpen: false });
    props.onClose?.();
  };

  const deletePreviewFile = (e?: React.SyntheticEvent) => {
    e?.stopPropagation();
    clearPreviewSession({ keepModalOpen: true });
    props.onDelete?.();
  };

  useEffect(() => {
    return () => {
      if (previewFile?.url?.startsWith("blob:")) URL.revokeObjectURL(previewFile.url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // UI
  return (
    <>
      <input
        type="file"
        accept={acceptAttr}
        ref={fileInputRef}
        onChange={handleFileInputChange}
        style={{ display: "none" }}
      />
      {!props.existingImage && (
        <div {...getRootProps()}>
          <input {...getInputProps()} />
          <div
            className={`drop-area ${isDragActive ? "drag-active" : ""}`}
            onClick={handleDropzoneClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && handleDropzoneClick(e as any)}
          >
            <div className="upload-icon">
              <CustomImage src={UploadIcon} alt="Cloud Upload Icon" />
            </div>

            <h6>Drag and Drop or Click to Upload</h6>
            <p>Choose a cover image or video for your article</p>
          </div>
          {!_.isEmpty(fileTypeError) && <span className="error-msg">{fileTypeError}</span>}
        </div>
      )}

      <Modal
        visible={showModal}
        onClose={closeModal}
        title={editMode ? `Edit cover ${previewFile?.mediaType || "media"}` : "Edit cover media"}
        className="article-cover-image-modal"
      >
        {!previewFile ? (
          <div className="cover-modal-body">
            <div className="cover-illustration" aria-hidden>📷</div>
            <h6>Choose a cover image or video for your article</h6>
            <Button onClick={() => fileInputRef.current?.click()}>Upload from computer</Button>
          </div>
        ) : (
          <div className="cover-modal-preview">
            {/* LEFT */}
            <div className="left-container">
              {!editMode && (
                <div className="small-preview-area">
                  {previewFile.mediaType === "video" ? (
                    <div className="image-preview-container">
                      {/* show video poster */}
                      {previewFile.videoThumbnail ? (
                        <img src={previewFile.videoThumbnail} alt="Video thumbnail" />
                      ) : (
                        <img src={previewFile.url} alt="Video" />
                      )}
                    </div>
                  ) : (
                    <div className="image-preview-container">
                      <img src={previewFile.url} alt="Small Preview" />
                    </div>
                  )}

                  {!editMode && (
                    <div className="delete-edit-icons">
                      <div className="delete-icon" onClick={deletePreviewFile} role="button" tabIndex={0}
                        onKeyDown={(e) => e.key === "Enter" && deletePreviewFile()}>
                        <DeleteOutlineOutlined />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {editMode && (
                <>
                  <div className={`options-bar ${previewFile.mediaType === "video" ? "thumbnail-options-bar" : ""}`}>
                    {previewFile.mediaType === "image" ? (
                      <>
                        <div
                          className={`option-button ${selectedOption === "crop" ? "active" : "inactive"}`}
                          onClick={() => setSelectedOption("crop")}
                        >
                          <CropIcon /><span>Crop</span>
                        </div>
                        <div
                          className={`option-button ${selectedOption === "filter" ? "active" : "inactive"}`}
                          onClick={() => setSelectedOption("filter")}
                        >
                          <FilterBAndWIcon /><span>Filter</span>
                        </div>
                        <div
                          className={`option-button ${selectedOption === "adjust" ? "active" : "inactive"}`}
                          onClick={() => setSelectedOption("adjust")}
                        >
                          <BrightnessMediumIcon /><span>Adjust</span>
                        </div>
                      </>
                    ) : (
                      <div className="video-thumbnail-section small-preview-area">
                        {previewFile.videoThumbnailFile ? (
                          <>
                            <div className="small-preview-area">
                              <div className="image-preview-container">
                                <img src={previewFile.videoThumbnail || ""} alt="Thumbnail preview" />
                              </div>
                            </div>
                            <div className="delete-edit-icons">
                              <div className="delete-icon" onClick={removeUploadedThumbnail} role="button" tabIndex={0}
                                onKeyDown={(e) => e.key === "Enter" && removeUploadedThumbnail()}>
                                <DeleteOutlineOutlined />
                              </div>
                            </div>
                          </>
                        ) : (
                          <div
                            className="video-thumails-upload"
                            role="button"
                            tabIndex={0}
                            onClick={openThumbPicker}
                            onKeyDown={(e) => e.key === "Enter" && openThumbPicker()}
                          >
                            <div className="upload-icon">
                              <CustomImage src={UploadIcon} alt="Cloud Upload Icon" />
                            </div>
                            <p>Drag and Drop or Click to Upload</p>
                            <input
                              type="file"
                              accept="image/*"
                              ref={thumbInputRef}
                              style={{ display: "none" }}
                              onChange={onThumbInputChange}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="edit-controls">
                    {/* CROP */}
                    {previewFile.mediaType === "image" && selectedOption === "crop" && (
                      <div className="crop-controls">
                        <div className="rotate-flip-section">
                          <h4>Rotate & Flip</h4>
                          <div className="control-buttons">
                            <button onClick={rotateLeft} className="control-btn" aria-label="Rotate left">
                              <RotateLeftIcon />
                            </button>
                            <button onClick={rotateRight} className="control-btn" aria-label="Rotate right">
                              <RotateRightIcon />
                            </button>
                            <button onClick={flipHorizontalToggle} className="control-btn" aria-label="Flip horizontal">
                              <FlipIcon />
                            </button>
                            <button
                              onClick={flipVerticalToggle}
                              className="control-btn"
                              aria-label="Flip vertical"
                              style={{ transform: "rotate(90deg)" }}
                            >
                              <FlipIcon />
                            </button>
                          </div>
                        </div>

                        <div className="slider-section">
                          <div className="label-row">
                            <label>Zoom</label>
                            <span className="value-chip">{zoomToUI(zoom)}</span>
                          </div>
                          <Slider value={zoomUI} min={0} max={100} step={1} onChange={(_, v) => setZoomUI(v as number)} />
                        </div>

                        <div className="slider-section">
                          <div className="label-row">
                            <label>Straighten</label>
                            <span className="value-chip">{straighten}°</span>
                          </div>
                          <Slider value={straighten} min={-45} max={45} step={1} onChange={(_, v) => setStraighten(v as number)} />
                        </div>
                      </div>
                    )}

                    {/* FILTER */}
                    {previewFile.mediaType === "image" && selectedOption === "filter" && (
                      <div className="filter-controls">
                        <div className="predefined-filters">
                          <div className="filter-grid">
                            {PREDEFINED_FILTERS.map((filter) => (
                              <div
                                key={filter.id}
                                className={`filter-option ${selectedFilter === filter.id ? "active" : ""}`}
                                onClick={() => applyPredefinedFilter(filter.id)}
                              >
                                <div className="filter-preview">
                                  <img
                                    src={getFilterPreviewImageUrl()}
                                    alt={filter.name}
                                    style={{
                                      filter: `brightness(${filter.settings.brightness}%) contrast(${filter.settings.contrast}%) saturate(${filter.settings.saturate}%) grayscale(${filter.settings.grayscale}%)`,
                                    }}
                                  />
                                </div>
                                <span className="filter-name">{filter.name}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* <div className="current-filter-info" style={{ marginTop: 10, fontSize: 12, color: "#666" }}>
                          <small>
                            Active filter: {PREDEFINED_FILTERS.find((f) => f.id === selectedFilter)?.name || "Original"}
                          </small>
                        </div> */}
                      </div>
                    )}

                    {/* ADJUST */}
                    {previewFile.mediaType === "image" && selectedOption === "adjust" && (
                      <div className="adjust-controls">
                        <div className="slider-section">
                          <div className="label-row">
                            <label>Brightness</label>
                            <span className="value-chip">{adjustBrightness}%</span>
                          </div>
                          <Slider value={adjustBrightness} min={0} max={200} step={1} onChange={(_, v) => setAdjustBrightness(v as number)} />
                        </div>
                        <div className="slider-section">
                          <div className="label-row">
                            <label>Contrast</label>
                            <span className="value-chip">{adjustContrast}%</span>
                          </div>
                          <Slider value={adjustContrast} min={0} max={200} step={1} onChange={(_, v) => setAdjustContrast(v as number)} />
                        </div>
                        <div className="slider-section">
                          <div className="label-row">
                            <label>Saturate</label>
                            <span className="value-chip">{adjustSaturate}%</span>
                          </div>
                          <Slider value={adjustSaturate} min={0} max={200} step={1} onChange={(_, v) => setAdjustSaturate(v as number)} />
                        </div>
                      </div>
                    )}
                  </div>

                  {previewFile.mediaType === "image" && (
                    <div className="reset-controls">
                      <Button onClick={onResetAll}>Reset All</Button>
                    </div>
                  )}
                </>
              )}


            </div>

            {/* RIGHT */}
            <div className="right-container">
              <div className="large-preview-area">
                {editMode ? (
                  previewFile.mediaType === "video" ? (
                    <video
                      ref={videoRef}
                      src={previewFile.url}
                      controls
                      poster={previewFile.videoThumbnail || undefined}
                      style={{ width: "100%", height: "100%", objectFit: "contain" }}
                    />
                  ) : (
                    <canvas
                      ref={editCanvasRef}
                      style={{ width: "100%", height: "100%", objectFit: "contain", touchAction: "none", display: "block", backgroundColor: "transparent" }}
                    />
                  )
                ) : previewFile.mediaType === "video" ? (
                  <video
                    ref={videoRef}
                    src={previewFile.url}
                    controls
                    poster={previewFile.videoThumbnail || undefined}
                    style={{ width: "100%", height: "100%", objectFit: "contain" }}
                  />
                ) : (
                  <img key={previewFile.url} src={previewFile.url} alt="Large Preview" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                )}
              </div>

              {!editMode && (
                <div className="edit-icon" onClick={onEditClick} role="button" tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && onEditClick()}>
                  <PenSVG />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Bottom bar */}
        {previewFile && (
          <div className="bottom-action-bar">
            {editMode ? (
              <>
                <Button color="secondary" className="action-cancel" onClick={onCancelEdit}>Cancel</Button>
                <Button className="action-apply" onClick={onApplyChanges}>Apply</Button>
              </>
            ) : (
              <Button
                className="action-next"
                onClick={() => {
                  props.onSuccess?.(
                    previewFile ? previewFile.file || null : null,
                    previewFile ? previewFile.originalFile || null : null,
                    previewFile?.mediaType,
                    previewFile?.mediaType === "video" ? previewFile.videoThumbnail : undefined,
                    previewFile?.editHistory
                  );
                  closeModal();
                }}
              >
                Done
              </Button>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

ArticleDropZone.defaultProps = {
  allowFileTypes: [],
  displayFileName: true,
  allowFileSize: 10 * 1024 * 1024,
};

export default ArticleDropZone;


